/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormActions,
  FormFlowState,
} from '@gh/builder-shared';
import {
  Month,
  OfficeMeasurement,
  OfficeMeasurementDefault,
  OfficeStatus,
  OfficeType,
  UserType,
} from '@gh/builder-shared-data';
import {
  ActionFlow,
  AppRouter,
  DictionaryItem,
  DictionaryService,
  EnumDictionary,
  getDefaultEmail,
  getRouteInitData,
  TextDictionary,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DEFAULT_EMAIL_SUFFIX } from '@gh/config';
import { MessageBasket } from '@gh/core-messages';
import {
  AutocompleteLoadMoreEvent,
  DialogService,
  FormMode,
  getControlValue,
  NotificationService,
  validationError,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { filter$, switchMap$ } from '@gh/rx/operators';

import { assign, filter, isEmpty, isNil } from 'lodash';

import { MasterZone } from '../../resources/master-zone.model';
import { OfficeDetailsBusinessService } from '../../resources/office-details-business.service';
import { OfficeDetails } from '../../resources/office-details.model';
import { OfficeListItem } from '../../resources/office-list-item.model';
import { OfficeActiveRecord } from 'builder-setup/offices/resources/office-active-record-model';
import { OfficeDetailsFormState } from './office-details-form.service';
import { AbstractControl, FormControl, ValidatorFn } from '@angular/forms';
import { TranslationService } from '@gh/core-mls';

@Component({
  selector:      'gh-office-details',
  template:   require('./office-details.component.html'),
  styles:     [require('./office-details.component.scss')],
  viewProviders: [OfficeDetailsFormState],
})
export class OfficeDetailsComponent extends AbstractFormComponent<OfficeDetails> implements OnInit {
  officeTypes: EnumDictionary<OfficeType>;
  officeStatuses: EnumDictionary<OfficeStatus>;
  masterZones: MasterZone[];
  states: DictionaryItem<string>[];
  templates: TextDictionary;
  measurements: EnumDictionary<OfficeMeasurement>;
  months: EnumDictionary<Month>;
  financialYears: number[];
  measurementDefaults: Hash<EnumDictionary<OfficeMeasurementDefault>>;
  emailSuffix = DEFAULT_EMAIL_SUFFIX;
  timeZones: TextDictionary;
  isModifyingOn: boolean = false;
  officeId: ID;
  activeCount: number = 0;   
  inactiveCount: number = 0;  
  private _currentTab: string = 'common';

  constructor(private route: ActivatedRoute,
    private router: AppRouter,
    private businessService: OfficeDetailsBusinessService,
    public form: OfficeDetailsFormState,
    private pipelines: FormActionPipelines,
    private dictionaryService: DictionaryService,
    private commonActions: CommonActions,
    private formActions: FormActions,
    private dialogService: DialogService,
    private navigationService: BuilderNavigationService,
    private userContext: BuilderUserContext,
    private notifications: NotificationService,
    private translate: TranslationService,
    private waitings: WaitingService,
  ) {
    super();
  }

  get title(): string {
    return getControlValue(this.form.group, 'name');
  }

  get email(): string {
    return getDefaultEmail(getControlValue(this.form.group, 'email'));
  }

  get pageTitle(): string {
    return {
      [FormMode.CreateMode]: 'lbl_office_create',
      [FormMode.EditMode]:   'lbl_office_edit',
      [FormMode.ViewMode]:   '',
    }[this.form.mode];
  }

  get currentTab(): string {
    return this._currentTab;
  }

  get completeFlow(): ActionFlow<FormFlowState<OfficeDetails>> {
    let invalidFormPath = '';
    if (this.form.group.controls['billing'].invalid) {
      invalidFormPath = 'billing';
    } else {
      if (this.form.group.invalid) {
        invalidFormPath = 'common';
      } else {
        invalidFormPath = '';
      }
    }
    if (!isEmpty(invalidFormPath)) {
      this.onTabChanged(invalidFormPath);
      if (!!this.form.group.controls[invalidFormPath]) {
        this.form.group.controls[invalidFormPath].markAllAsTouched();
      }
    }
    return this.pipelines.complete({
      validate: this.commonActions.validate(
        (state: FormFlowState<OfficeDetails>) => this.validateBeforeProcess(state.value),
      ),
      execute:  this.commonActions.execute((state: FormFlowState<OfficeDetails>) => {
        if (this.form.createMode) {
          return this.businessService.create(this.prepareSubmitPayload(state.form.submitValue));
        } else {
          return this.businessService.update(this.prepareSubmitPayload(state.form.submitValue)); 
        }
      }),
      finish:   (state) => {
        const { value, form } = state;
        this.isModifyingOn = false;
        if (this.form.createMode) {
          this.router.switchTo(['/setup/offices', value.id]);
        } else {
          this.form.refreshFinancialYearInformationSchema();
          form.build(value);
        }
      },
    });
  }

  ngOnInit(): void {
    const { officeDetails, states,  ...init } = getRouteInitData(this.route);
    this.states = states.seq().sort((a: { name: string; id: ID }, b: { name: string; id: ID }) => {
      if (a.name.toLowerCase() === 'overseas') {
        return 1;
      }
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });
    assign(this, init);
    if (officeDetails && officeDetails.statusValue === OfficeStatus.InDeletion) {
      this.router.navigateBack();
    }

    this.officeId = officeDetails.id;

    this.form.build(officeDetails);
    this.form.group.get('billing.officeClosed')?.setValidators(
      this.dateRangeValidator(this.form.group.get('billing.contractSignedDate'), 'lbl_contract_signed_date'),
    );
    this.form.group.get('billing.renewalDate')?.setValidators(
      this.dateRangeValidator(this.form.group.get('billing.originalCommencement'), 'lbl_original_commencement'),
    );
    this.form.group.get('billing.currentAgreement')?.setValidators(
      this.dateRangeValidator(this.form.group.get('billing.originalCommencement'), 'lbl_original_commencement', true),
    );
    this.form.group.get('billing.onSellDate')?.setValidators(
      this.dateRangeValidator(this.form.group.get('billing.contractSignedDate'), 'lbl_contract_signed_date'),
    );
    this.form.group.get('type')?.valueChanges.subscribe((type) => {
      if (type === OfficeType.Training) {
        this.form.group.get('publishToWeb')?.setValue(false);
        this.form.group.get('publishToWeb')?.disable();
      } else {
        this.form.group.get('publishToWeb')?.enable();
      }
    });
    this.form.group.addControl('includeInactive', new FormControl(false));
    if (officeDetails.type === OfficeType.Training) {
      this.form.group.get('publishToWeb')?.disable();
    }
  }

  minDate(type: string): Date {
    return this.form.get(type)?.value;
  }

  minRenewalDate(): Date {
    const currentAgreement = this.form.get('billing.currentAgreement')?.value;
    const originalCommencement = this.form.get('billing.originalCommencement')?.value;
    if (currentAgreement && originalCommencement) {
      return currentAgreement.getTime() > originalCommencement.getTime() ? currentAgreement : originalCommencement;
    } else {
      if (!currentAgreement && originalCommencement) {
        return originalCommencement;
      } else {
        if (!originalCommencement && currentAgreement) {
          return currentAgreement;
        } else {
          // tslint:disable-next-line:no-magic-numbers
          return new Date(1970);
        }
      }
    }
  }

  onSystemNameChanged(): void {
    this.form.updateEmail();
  }

  onApplyLicense(): void {
    this.form.updateLicenseState();
  }

  onMasterZoneChange(): void {
    this.form.updateTimeZone();
  }

  onMeasurementChange(): void {
    this.form.updateMeasurementState();
  }

  onTabChanged(name: string): void {
    if (this._currentTab !== name) {
      this._currentTab = name;
    }
  }

  onAssignmentFeeUpdate(): void {
    this.form.onRecalculateAssignmentFee();
  }

  onRenewalDateUpdate(): void {
    this.form.onRecalculateRenewalDate();
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId:   entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'OFFICE',
      label:      label ? label : 'lbl_menu_offices',
    });
  }

  loadOffices($event: AutocompleteLoadMoreEvent): void {
    this.businessService.findOfficesByName($event.query).subscribe((offices: any[]) => {
      const doNotImportOption = { name: 'DO NOT IMPORT DATA' }; 
      offices.unshift(doNotImportOption);  
  
      $event.load(offices);
    });
  }

  onSourceOfficeChange(office: OfficeListItem): void {
    if (office.name === "DO NOT IMPORT DATA") return ;
    const officeActiveRecord = this.mapToOfficeActiveRecord(office);
    this.businessService.findActiveRecordsByOfficeName(officeActiveRecord).subscribe((value) => {
      this.activeCount = value.activeRecordsCount;  // Update active count
      this.inactiveCount = value.inactiveRecordsCount;
    });
    if (isNil(office)) {
      this.form.resetTemplates();
    }
  }

  mapToOfficeActiveRecord(office: OfficeListItem): OfficeActiveRecord {
    return {
      officeId: office.officeId,
      statusValue: office.statusValue,
      activeRecordsCount: 0,
      inactiveRecordsCount: 0,
    };
  }

  isSourceOfficeValid(): boolean {
    const sourceOfficeValue = this.form.group.get('sourceOffice')?.value;
    return sourceOfficeValue && sourceOfficeValue.name !== 'DO NOT IMPORT DATA';
  }

  onInactiveRecordsToggle(checked: boolean): void {
    this.form.group.get('includeInactive')?.setValue(checked);
  }

  isTemplateChecked(name: string): boolean {
    return this.form.isTemplateChecked(name);
  }

  isActivateVisible(): boolean {
    const status = this.officeStatuses.get(this.form.get('status').value);
    return this.form.editMode &&
      this.userContext.userType === UserType.Corporate &&
      status === OfficeStatus.Blocked;
  }

  isBlockVisible(): boolean {
    const status = this.officeStatuses.get(this.form.get('status').value);
    return this.form.editMode &&
      this.userContext.userType === UserType.Corporate &&
      status === OfficeStatus.Active;
  }

  isTemplatePartlyChecked(name: string): boolean {
    return this.form.isTemplatePartlyChecked(name);
  }

  onTemplateChecked(name: string, checked: boolean): void {
    this.form.markChildTemplates(name, checked);
  }

  onModifyFinancialInformation(): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_accounting_data_edit')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.enableFinancialInformationControls();
        this.isModifyingOn = true;
      });
  }

  onBlockOffice(): void {
    if (this.form.dirty) {
      this.notifications.error('msg_save_first');
    } else {
      this.dialogService.confirmation(yesNoConfirmation('msg_office_block_confirmation')).pipe(
            filter$(yesAnswer),
            switchMap$(() => this.businessService.blockOffice(this.officeId).pipe(this.waitings.operator())))
          .subscribe(
            (value) => this.form.build(value),
            this.notifications.processAll);
    }
  }

  onActivateOffice(): void {
    if (this.form.dirty) {
      this.notifications.error('msg_save_first');
    } else {
      this.businessService.activateOffice(this.officeId).pipe(
        this.waitings.operator())
        .subscribe(
          (value) => {
            this.form.build(value);
          },
          this.notifications.processAll,
        );
    }
  }

  dateRangeValidator(minValueControl: any, label: string, orEqual?: boolean): ValidatorFn {
    const fieldName = this.translate.get(label);
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control?.value;
      const minValue = minValueControl?.value;
      const errorMessage = orEqual ? 'msg_incorrect_date_range_equal' : 'msg_incorrect_date_range';
      const error = {
        incorrectDate: validationError(errorMessage, {
          name: fieldName,
        }),
      };
      if ((
        orEqual && minValue && value && minValue > value
      ) || (
        !orEqual && minValue && value && minValue >= value
      )) {
        setTimeout(() => {
          control.setErrors(error);
        },         0);
      }
      // tslint:disable-next-line:no-null-keyword
      return null;
    };
  }

  private validateBeforeProcess(value: OfficeDetails): Maybe<MessageBasket> {
    if (this.form.createMode 
      && value.sourceOffice && value.sourceOffice.name !== 'DO NOT IMPORT DATA' && isEmpty(filter(value.templateFlatHierarchy, (template) => template.checked))) {
      return MessageBasket.error('msg_office_no_template_selected');
    }
  }

  private prepareSubmitPayload(submitValue: any): any {
    if (submitValue.sourceOffice && submitValue.sourceOffice.name === 'DO NOT IMPORT DATA') {
      submitValue.sourceOffice = null;
    }
    return submitValue;
  }
}
